import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Eye, CheckCircle2, XCircle, Users } from "lucide-react";

export function FantaPredictionsList({ refreshKey }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (md) => {
    setLoading(true);
    try {
      const r = await api.get("/fantapadel/predictions", { params: md ? { matchday: md } : {} });
      setData(r.data);
      setSelected(r.data.matchday);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (loading && !data) {
    return (
      <div className="ap-card p-5 flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" />
      </div>
    );
  }

  const preds = data?.predictions || [];

  return (
    <div className="ap-card p-5" data-testid="fanta-predictions-section">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Eye className="w-5 h-5 text-cyan-400" />
        <h3 className="font-display uppercase tracking-wide text-lg text-white">Pronostici dei tifosi</h3>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
          <Users className="w-3.5 h-3.5" /> {preds.length}
        </span>
      </div>

      {(data?.available?.length || 0) > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {data.available.map((n) => (
            <button
              key={n}
              data-testid={`fanta-pred-md-${n}`}
              onClick={() => load(n)}
              className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border transition-colors ${
                selected === n ? "bg-fuchsia-500 border-fuchsia-400 text-white" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500"
              }`}
            >
              G{n}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-400" /></div>
      ) : preds.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">
          Nessun pronostico per questa giornata. Sii il primo!
        </div>
      ) : (
        <div className="space-y-3">
          {data.completed && (
            <p className="text-[11px] text-slate-500">Giornata conclusa · ✓ vincitore indovinato, bordo pieno = risultato esatto.</p>
          )}
          {preds.map((p, i) => (
            <div key={`${p.nickname}-${i}`} data-testid={`fanta-pred-row-${i}`} className="rounded-xl bg-slate-950/50 border border-slate-800 p-3">
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-sm font-semibold text-white truncate">{p.nickname}</span>
                {data.completed && (
                  <span className="shrink-0 font-mono text-xs text-slate-400">
                    {p.exact > 0 && <span className="text-amber-400">{p.exact} esatti · </span>}
                    <span className="font-black text-emerald-400">{p.points} pt</span>
                  </span>
                )}
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                {data.matches.map((m) => {
                  const pk = p.picks[String(m.court)];
                  if (!pk) return <div key={m.court} className="text-xs text-slate-600 px-2 py-1.5">Campo {m.court}: —</div>;
                  const winner = pk.winner === "team1" ? m.team1 : m.team2;
                  const score = (pk.sets || []).map((s) => `${s.g1}-${s.g2}`).join("  ");
                  let correct = null, exact = false;
                  if (data.completed && m.result) {
                    correct = pk.winner === m.result.winner;
                    exact = JSON.stringify(pk.sets) === JSON.stringify(m.result.sets);
                  }
                  return (
                    <div
                      key={m.court}
                      className={`text-xs rounded-lg px-2.5 py-1.5 border ${
                        exact ? "border-emerald-500 bg-emerald-500/15"
                        : correct === true ? "border-emerald-500/40 bg-emerald-500/5"
                        : correct === false ? "border-rose-500/30 bg-rose-500/5"
                        : "border-slate-800 bg-slate-900/40"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wider text-slate-500">Campo {m.court}</span>
                        {correct === true && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                        {correct === false && <XCircle className="w-3 h-3 text-rose-400" />}
                      </div>
                      <div className="text-slate-100 font-semibold truncate">{winner.join(" / ")}</div>
                      <div className="font-mono text-slate-400">{score}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
